import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import type { DraggableAttributes } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import GripHorizontal from 'lucide-react/dist/esm/icons/grip-horizontal';
import Star from 'lucide-react/dist/esm/icons/star';
import Globe from 'lucide-react/dist/esm/icons/globe';
import Sparkles from 'lucide-react/dist/esm/icons/sparkles';
import Radar from 'lucide-react/dist/esm/icons/radar';
import { Card, Text } from 'components/ui';
import { useUIStore } from 'stores';
import { getTranslation } from 'i18n';
import { useSalesStore } from '../../stores/salesStore';
import type { SalesLead } from '../../types/sales';

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  return `${days}d`;
}

function Stars({
  count,
  max = 5,
  size = 12,
  color = 'purple',
}: {
  count: number | null;
  max?: number;
  size?: number;
  color?: 'amber' | 'purple';
}) {
  const filled = color === 'purple' ? 'text-purple-400 fill-purple-400' : 'text-amber-400 fill-amber-400';
  const n = count ?? 0;
  return (
    <span className="inline-flex gap-px">
      {Array.from({ length: max }, (_, i) => (
        <Star
          key={i}
          size={size}
          className={i < n ? filled : 'text-content-faint'}
          strokeWidth={1.5}
        />
      ))}
    </span>
  );
}

export type LeadCardDragProps = {
  setNodeRef: (node: HTMLElement | null) => void;
  style: React.CSSProperties;
  attributes: DraggableAttributes;
  listeners: Record<string, unknown> | undefined;
  isDragging: boolean;
};

interface LeadCardContentProps {
  lead: SalesLead;
  /** When set, card is sortable; omit for drag overlay (must not register a second sortable id). */
  drag?: LeadCardDragProps;
  isDragOverlay?: boolean;
}

const LeadCardContent: React.FC<LeadCardContentProps> = ({ lead, drag, isDragOverlay }) => {
  const openLeadModal = useSalesStore((s) => s.openLeadModal);
  const language = useUIStore((s) => s.language);
  const t = (key: string) => getTranslation(language, key);

  const handleClick = () => {
    if (drag?.isDragging) return;
    openLeadModal(lead.id);
  };

  let hostname = '';
  try {
    if (lead.website_url) hostname = new URL(lead.website_url.startsWith('http') ? lead.website_url : `https://${lead.website_url}`).hostname;
  } catch { /* ignore */ }

  const handleGripClass =
    'transition-opacity cursor-grab active:cursor-grabbing touch-none text-content-muted hover:text-content-secondary inline-flex items-center justify-center min-w-9 py-1 rounded-md hover:bg-surface-secondary/80';
  const gripVisible = isDragOverlay ? 'opacity-100' : 'opacity-0 group-hover:opacity-100';

  return (
    <div
      ref={drag?.setNodeRef}
      style={drag?.style}
      className={`group ${drag?.isDragging ? 'opacity-40' : ''} ${isDragOverlay ? 'rotate-2 shadow-lg' : ''}`}
    >
      <Card
        className="!p-3 cursor-pointer hover:border-default transition-all duration-150"
        onClick={handleClick}
      >
        <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-x-2 mb-1.5">
          <Text variant="subheading" className="text-sm leading-tight truncate min-w-0">
            {lead.company_name}
          </Text>
          <div
            onClick={(e) => e.stopPropagation()}
            className="justify-self-center"
          >
            <div
              {...(drag && !isDragOverlay ? drag.attributes : {})}
              {...(drag && !isDragOverlay ? drag.listeners : {})}
              className={`${handleGripClass} ${gripVisible}`}
              aria-label={t('salesLeadCardDragHandle')}
            >
              <GripHorizontal size={14} strokeWidth={2} className="pointer-events-none shrink-0" />
            </div>
          </div>
          <div className="flex justify-end items-center min-w-0">
            {lead.source === 'ai_generated' && (
              <Radar
                size={15}
                className="text-purple-500"
                strokeWidth={2}
                aria-label={t('salesSidebarGeneratorLabel')}
              />
            )}
          </div>
        </div>

        {hostname && (
          <div className="flex items-center gap-1 mb-1.5">
            <Globe size={11} className="text-content-muted flex-shrink-0" />
            <span className="text-xs text-content-muted truncate">{hostname}</span>
          </div>
        )}

        <p
          className="text-xs text-content-secondary line-clamp-2 leading-relaxed min-h-[2lh] mb-1.5"
          aria-hidden={!lead.inquiry}
        >
          {lead.inquiry ?? ''}
        </p>

        <div className="flex flex-col gap-px">
          <div className="flex items-center gap-1" title={t('salesLeadCardAiRatingTitle')}>
            <Stars count={lead.ai_rating} size={10} />
            <Sparkles size={11} className="text-purple-400 flex-shrink-0" aria-hidden />
          </div>
          <div className="flex items-center justify-between gap-2">
            <Stars count={lead.sales_rating} size={10} color="amber" />
            <span className="text-[10px] text-content-muted shrink-0 tabular-nums">
              {timeAgo(lead.created_at)}
            </span>
          </div>
        </div>
      </Card>
    </div>
  );
};

function LeadCardSortable({ lead }: { lead: SalesLead }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: lead.id });

  const drag: LeadCardDragProps = {
    setNodeRef,
    style: {
      transform: CSS.Transform.toString(transform),
      transition,
    },
    attributes,
    listeners,
    isDragging,
  };

  return <LeadCardContent lead={lead} drag={drag} />;
}

interface LeadCardProps {
  lead: SalesLead;
  isDragOverlay?: boolean;
}

const LeadCard: React.FC<LeadCardProps> = ({ lead, isDragOverlay }) => {
  if (isDragOverlay) {
    return <LeadCardContent lead={lead} isDragOverlay />;
  }
  return <LeadCardSortable lead={lead} />;
};

export default React.memo(LeadCard);
export { Stars };
